import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../axios-instance";

export type UserNotificationCommentDto = {
  UserId: string;
  Comment: string;
  CommentId: string;
  Timestamp: string;
};

type RawUserNotificationCommentDto = Partial<UserNotificationCommentDto> & {
  userId?: string;
  comment?: string;
  commentId?: string;
  timestamp?: string;
};

export type UserNotificationDto = {
  AlarmId: string;
  Message: string;
  Description: string;
  Timestamp: string;
  Instance: string;
  Read: boolean;
  TypeString: string;
  ChartUrl: string | null;
  EquipmentPath: string;
  Acknowledged: boolean;
  AcknowledgedBy: string | null;
  AcknowledgedAt: string | null;
  Muted: boolean;
  MutedUntil: string | null;
  Comments: UserNotificationCommentDto[];
};

type RawUserNotificationDto = Partial<UserNotificationDto> & {
  alarmId?: string;
  message?: string;
  description?: string;
  timestamp?: string;
  instance?: string;
  read?: boolean;
  typeString?: string;
  chartUrl?: string | null;
  equipmentPath?: string;
  acknowledged?: boolean;
  acknowledgedBy?: string | null;
  acknowledgedAt?: string | null;
  muted?: boolean;
  mutedUntil?: string | null;
  comments?: RawUserNotificationCommentDto[];
};

export type NotificationCoverage = 0 | 1 | 2 | "none" | "partial" | "all";

export type UserNotificationTypeDto = {
  NotificationTypeId: number;
  NotificationTypeName: string;
  Category: string;
  Push: boolean;
  Coverage: NotificationCoverage;
};

export type UserNotificationTypesDto = {
  Source: number | string;
  NotificationTypes: UserNotificationTypeDto[];
};

export type UserNotificationTypeUpdateDto = {
  NotificationTypeId: number;
  Enabled: boolean;
  Push: boolean;
};

type RawUserNotificationTypeDto = Partial<UserNotificationTypeDto> & {
  notificationTypeId?: number;
  notificationTypeName?: string;
  category?: string;
  push?: boolean;
  coverage?: NotificationCoverage;
};

type RawUserNotificationTypesDto = Partial<UserNotificationTypesDto> & {
  source?: number | string;
  notificationTypes?: RawUserNotificationTypeDto[];
};

function normalizeUserNotificationDto(
  notification: RawUserNotificationDto
): UserNotificationDto {
  return {
    AlarmId: notification.AlarmId ?? notification.alarmId ?? "",
    Message: notification.Message ?? notification.message ?? "",
    Description: notification.Description ?? notification.description ?? "",
    Timestamp: notification.Timestamp ?? notification.timestamp ?? "",
    Instance: notification.Instance ?? notification.instance ?? "",
    Read: notification.Read ?? notification.read ?? false,
    TypeString: notification.TypeString ?? notification.typeString ?? "",
    ChartUrl: notification.ChartUrl ?? notification.chartUrl ?? null,
    EquipmentPath:
      notification.EquipmentPath ?? notification.equipmentPath ?? "",
    Acknowledged:
      notification.Acknowledged ?? notification.acknowledged ?? false,
    AcknowledgedBy:
      notification.AcknowledgedBy ?? notification.acknowledgedBy ?? null,
    AcknowledgedAt:
      notification.AcknowledgedAt ?? notification.acknowledgedAt ?? null,
    Muted: notification.Muted ?? notification.muted ?? false,
    MutedUntil: notification.MutedUntil ?? notification.mutedUntil ?? null,
    Comments: (notification.Comments ?? notification.comments ?? []).map(
      normalizeUserNotificationCommentDto
    ),
  };
}

function normalizeUserNotificationCommentDto(
  comment: RawUserNotificationCommentDto
): UserNotificationCommentDto {
  return {
    UserId: comment.UserId ?? comment.userId ?? "",
    Comment: comment.Comment ?? comment.comment ?? "",
    CommentId: comment.CommentId ?? comment.commentId ?? "",
    Timestamp: comment.Timestamp ?? comment.timestamp ?? "",
  };
}

function normalizeUserNotificationTypeDto(
  notificationType: RawUserNotificationTypeDto
): UserNotificationTypeDto {
  return {
    NotificationTypeId:
      notificationType.NotificationTypeId ??
      notificationType.notificationTypeId ??
      0,
    NotificationTypeName:
      notificationType.NotificationTypeName ??
      notificationType.notificationTypeName ??
      "",
    Category: notificationType.Category ?? notificationType.category ?? "Other",
    Push: notificationType.Push ?? notificationType.push ?? false,
    Coverage: (notificationType.Coverage ??
      notificationType.coverage ??
      "none") as NotificationCoverage,
  };
}

function normalizeUserNotificationTypesDto(
  notifications: RawUserNotificationTypesDto
): UserNotificationTypesDto {
  const rawTypes =
    notifications.NotificationTypes ?? notifications.notificationTypes ?? [];

  return {
    Source: notifications.Source ?? notifications.source ?? "",
    NotificationTypes: rawTypes.map(normalizeUserNotificationTypeDto),
  };
}

const keys = {
  unreadCount: (customer?: string) =>
    [customer, "user-notifications", "unread-count"] as const,
  notifications: (customer?: string) =>
    [customer, "user-notifications"] as const,
  notificationTypes: (customer?: string) =>
    [customer, "user-notification-types"] as const,
};

const urls = {
  unreadCount: (customer: string) =>
    `/customers/${customer}/user-notifications/unread-count`,
  notifications: (customer: string) =>
    `/customers/${customer}/user-notifications`,
  markRead: (customer: string) =>
    `/customers/${customer}/user-notifications/read`,
  notificationTypes: (customer: string) =>
    `/customers/${customer}/user-notification-types`,
};

export function useUnreadNotificationsCount(customer?: string) {
  return useQuery<number>({
    queryKey: keys.unreadCount(customer),
    queryFn: async () => {
      if (!customer) return 0;
      return (await axiosInstance.get<number>(urls.unreadCount(customer))).data;
    },
    enabled: !!customer,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useUserNotifications(
  customer?: string,
  enabled: boolean = false
) {
  return useQuery<UserNotificationDto[]>({
    queryKey: keys.notifications(customer),
    queryFn: async () => {
      if (!customer) return [];

      const response = await axiosInstance.get<RawUserNotificationDto[]>(
        urls.notifications(customer)
      );

      return (response.data ?? []).map(normalizeUserNotificationDto);
    },
    enabled: !!customer && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useUserNotificationTypes(
  customer?: string,
  enabled: boolean = false
) {
  return useQuery<UserNotificationTypesDto>({
    queryKey: keys.notificationTypes(customer),
    queryFn: async () => {
      if (!customer) {
        return { Source: "", NotificationTypes: [] };
      }

      const response = await axiosInstance.get<RawUserNotificationTypesDto>(
        urls.notificationTypes(customer)
      );

      return normalizeUserNotificationTypesDto(response.data ?? {});
    },
    enabled: !!customer && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useMarkNotificationsRead(customer?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alarmIds: string[]) => {
      if (!customer || alarmIds.length === 0) {
        return alarmIds;
      }

      await axiosInstance.put(urls.markRead(customer), alarmIds);
      return alarmIds;
    },
    onSuccess: (alarmIds) => {
      if (!customer || !alarmIds || alarmIds.length === 0) {
        return;
      }

      queryClient.setQueryData<UserNotificationDto[]>(
        keys.notifications(customer),
        (current = []) =>
          current.map((notification) =>
            alarmIds.includes(notification.AlarmId)
              ? { ...notification, Read: true }
              : notification
          )
      );

      queryClient.setQueryData<number>(
        keys.unreadCount(customer),
        (current = 0) => Math.max(0, current - alarmIds.length)
      );
    },
  });
}

export function useSaveUserNotificationTypes(customer?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      notificationTypeUpdates: UserNotificationTypeUpdateDto[]
    ) => {
      if (!customer) {
        return;
      }

      await axiosInstance.post(
        urls.notificationTypes(customer),
        notificationTypeUpdates
      );
    },
    onSuccess: (_result, notificationTypeUpdates) => {
      if (!customer || !notificationTypeUpdates?.length) {
        return;
      }

      const updatesById = new Map(
        notificationTypeUpdates.map((item) => [item.NotificationTypeId, item])
      );

      queryClient.setQueryData<UserNotificationTypesDto | undefined>(
        keys.notificationTypes(customer),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            Source: "UserOverride",
            NotificationTypes: current.NotificationTypes.map((item) => {
              const update = updatesById.get(item.NotificationTypeId);
              if (!update) {
                return item;
              }

              return {
                ...item,
                Push: update.Enabled ? update.Push : false,
                Coverage: update.Enabled ? "all" : "none",
              };
            }),
          };
        }
      );
    },
  });
}

export function useResetUserNotificationTypes(customer?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!customer) {
        return;
      }

      await axiosInstance.delete(urls.notificationTypes(customer));
    },
    onSuccess: () => {
      if (!customer) {
        return;
      }

      queryClient.setQueryData<UserNotificationTypesDto | undefined>(
        keys.notificationTypes(customer),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            Source: "JobRole",
          };
        }
      );
    },
  });
}
